/**
 * Knowledge Base Admin API endpoints and types
 *
 * Provides access to the KB management endpoints for creating, updating,
 * and managing knowledge base entries.
 */

import apiClient from './client';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

/**
 * Backend canonical-question source-type vocabulary. `Qualitative`/
 * `Quantitative`/`Both`/`Attachment` are human-authored; `Exact`/`VaultData`/
 * `Generated` are pipeline-internal but may appear on existing rows.
 */
export type KbSourceType =
  | 'Qualitative'
  | 'Quantitative'
  | 'Attachment'
  | 'Both'
  | 'Exact'
  | 'VaultData'
  | 'Generated';

/**
 * A single knowledge base entry, matching the backend `EntryResponse`.
 * `firm_id` null/0 means a global entry; use `isFirmSpecific()` from
 * `features/admin/kb/kbDisplay` to derive scope.
 */
export interface KbEntry {
  id: number;
  vector_id: string;
  question_text: string;
  response_text: string | null;
  source_type: KbSourceType;
  category: string | null;
  tags: string[];
  firm_id: number | null;
  usage_count: number;
  last_used_at: string | null;
  accept_rate: number | null;
  version: number;
  created_by: string;
  created_at: string;
  updated_by: string | null;
  updated_at: string;
  is_stale: boolean;
  stale_reason: string | null;
}

export interface KbEntryListResponse {
  entries: KbEntry[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Aggregate statistics (backend `StatsResponse`). `by_source_type` is keyed
 * by the raw source-type string. The optional fields are NOT returned by the
 * backend - keep them optional so consumers guard.
 */
export interface KbStats {
  total_entries: number;
  stale_entries: number;
  by_category: Record<string, number>;
  by_source_type: Record<string, number>;
  firm_specific_entries?: number;
  global_entries?: number;
  recent_updates?: number;
  avg_response_length?: number;
}

export interface CreateEntryRequest {
  question_text: string;
  response_text: string;
  source_type?: KbSourceType;
  category?: string | null;
  tags?: string[];
  firm_id?: number | null;
}

export interface UpdateEntryRequest {
  question_text?: string;
  response_text?: string;
  category?: string | null;
  tags?: string[];
}

/**
 * Result returned by `checkDuplicates`. `similar_entries[].id` matches the
 * backend `kb_service` shape (`{ id, question_text, similarity_score }`).
 */
export interface DuplicateCheckResult {
  is_duplicate: boolean;
  similar_entries: Array<{
    id: number;
    question_text: string;
    similarity_score: number;
  }>;
}

/** Result of `refreshEmbeddings` (backend `RefreshResponse`). */
export interface RefreshEmbeddingsResult {
  total_processed: number;
  refreshed: number;
  errors: number;
}

export interface KbEntryFilters {
  category?: string | null;
  source_type?: KbSourceType | null;
  is_stale?: boolean | null;
  search?: string | null;
  firm_id?: number | null;
  taxonomy_category?: string | null;
  offset?: number;
  limit?: number;
}

// -----------------------------------------------------------------------------
// API Endpoints
// -----------------------------------------------------------------------------

const KB_BASE = '/v1/insight/admin/kb';

/**
 * List knowledge base entries with optional filters
 */
export async function listKbEntries(filters?: KbEntryFilters): Promise<KbEntryListResponse> {
  const params: Record<string, string | number | boolean> = {};

  if (filters?.category) params.category = filters.category;
  if (filters?.source_type) params.source_type = filters.source_type;
  if (filters?.is_stale !== undefined && filters.is_stale !== null) {
    params.is_stale = filters.is_stale;
  }
  if (filters?.search) params.search = filters.search;
  if (filters?.taxonomy_category) params.taxonomy_category = filters.taxonomy_category;
  if (filters?.firm_id !== undefined && filters.firm_id !== null) {
    params.firm_id = filters.firm_id;
  }
  if (filters?.offset !== undefined) params.offset = filters.offset;
  if (filters?.limit !== undefined) params.limit = filters.limit;

  const response = await apiClient.get<KbEntryListResponse>(`${KB_BASE}/entries`, { params });
  return response.data;
}

/**
 * Get a single knowledge base entry by ID
 */
export async function getKbEntry(entryId: number): Promise<KbEntry> {
  const response = await apiClient.get<KbEntry>(`${KB_BASE}/entries/${entryId}`);
  return response.data;
}

/**
 * Create a new knowledge base entry
 */
export async function createKbEntry(request: CreateEntryRequest): Promise<KbEntry> {
  const response = await apiClient.post<KbEntry>(`${KB_BASE}/entries`, request);
  return response.data;
}

/**
 * Update an existing knowledge base entry
 */
export async function updateKbEntry(entryId: number, request: UpdateEntryRequest): Promise<KbEntry> {
  const response = await apiClient.put<KbEntry>(`${KB_BASE}/entries/${entryId}`, request);
  return response.data;
}

/**
 * Soft delete a knowledge base entry
 */
export async function deleteKbEntry(entryId: number): Promise<void> {
  await apiClient.delete(`${KB_BASE}/entries/${entryId}`);
}

/**
 * Mark an entry as stale (needs review/update)
 */
export async function markEntryStale(entryId: number, reason: string): Promise<KbEntry> {
  const response = await apiClient.post<KbEntry>(`${KB_BASE}/entries/${entryId}/mark-stale`, { reason });
  return response.data;
}

/**
 * Clear stale flag from an entry
 */
export async function clearEntryStale(entryId: number): Promise<KbEntry> {
  const response = await apiClient.post<KbEntry>(`${KB_BASE}/entries/${entryId}/clear-stale`);
  return response.data;
}

/**
 * Check for duplicate entries before creating
 */
export async function checkDuplicates(questionText: string): Promise<DuplicateCheckResult> {
  const response = await apiClient.post<DuplicateCheckResult>(`${KB_BASE}/check-duplicates`, {
    question_text: questionText,
  });
  return response.data;
}

/**
 * Get distinct categories in the knowledge base.
 *
 * The backend contract is a bare array (admin_kb.py declares
 * `response_model=list[str]`); parsing `.categories` here returned
 * undefined and crashed the KB page.
 */
export async function getKbCategories(): Promise<string[]> {
  const response = await apiClient.get<string[]>(`${KB_BASE}/categories`);
  return response.data;
}

/**
 * Get knowledge base statistics
 */
export async function getKbStats(firmId?: number | null): Promise<KbStats> {
  const params: Record<string, number> = {};
  if (typeof firmId === 'number' && firmId > 0) {
    params.firm_id = firmId;
  }
  const response = await apiClient.get<KbStats>(`${KB_BASE}/stats`, { params });
  return response.data;
}

/**
 * Refresh embeddings for all or selected entries
 */
export async function refreshEmbeddings(entryIds?: number[]): Promise<RefreshEmbeddingsResult> {
  const response = await apiClient.post<RefreshEmbeddingsResult>(`${KB_BASE}/refresh-embeddings`, {
    entry_ids: entryIds,
  });
  return response.data;
}
